import React, { useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Check, CheckCircle2, Pencil } from "lucide-react";
import QuestionInput, { type Question } from "./QuestionInput";
import Button from "../ui/Button";
import InputField from "../ui/InputField";

type AnswersState = Record<string, string | string[]>;

interface ClientInfo {
  respondentName: string;
  respondentEmail: string;
  respondentPhone: string;
}

interface CardLayoutProps {
  name: string;
  description?: string;
  questions: Question[];
  answers: AnswersState;
  onChange: (id: string, val: string | string[]) => void;
  // Interactive (client) mode passes these; preview mode omits them.
  clientInfo?: ClientInfo;
  setClientInfo?: React.Dispatch<React.SetStateAction<ClientInfo>>;
  onSubmit?: () => void;
  isSubmitting?: boolean;
  preview?: boolean;
}

const isAnswered = (val: string | string[] | undefined) =>
  Array.isArray(val) ? val.length > 0 : val !== "" && val !== null && val !== undefined;

const emailOk = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

// A step is the contact card, a named section holding several questions, a
// single unsectioned question, or the final review.
//
// Sections exist because the guided intakes are written as a conversation —
// "Tell us about your brand", then the questions that belong under it. Showing
// those one at a time with no heading loses the thing that made them readable:
// the client gets question 14 of 40 with no idea what it is for.
type Step =
  | { type: "contact" }
  | { type: "section"; eyebrow: string; title: string; intro: string; questions: Question[] }
  | { type: "question"; q: Question; qIndex: number }
  | { type: "review" };

// Grouping is by `section` — the short numbered label, e.g. "1 · Your brand" —
// which every question in a section carries.
//
// NOT by `sectionTitle`. That is the big heading ("Tell us about your brand")
// and it is set only on the FIRST question of each section, so grouping on it
// split every section in two: one step for the opening question and another
// for the rest. Eight sections came out as fifteen steps.
const sectionOf = (q: Question) =>
  String((q as any).section || (q as any).sectionTitle || "").trim();

const CardLayout: React.FC<CardLayoutProps> = ({
  name,
  description,
  questions,
  answers,
  onChange,
  clientInfo,
  setClientInfo,
  onSubmit,
  isSubmitting,
  preview = false,
}) => {
  const collectContact = !preview && !!clientInfo && !!setClientInfo;

  // ── Conditional questions ─────────────────────────────────────────────────
  //
  // A question with a showWhen rule appears only when an earlier answer
  // matches. This used to run in the form layout only, so a client filling the
  // card layout was walked through every branch of the questionnaire — all the
  // brochure questions even when they had asked for a flyer alone. With 29
  // conditional questions in the Print Materials intake that is most of it.
  //
  // Recomputed from the current answers, so choosing "flyer only" removes the
  // brochure steps as you go rather than on a reload.
  const visible = useMemo(() => {
    const byKey: Record<string, string | string[]> = {};
    for (const q of questions) {
      const k = String((q as any).key || "");
      if (k) byKey[k] = answers[q._id] ?? "";
    }
    return questions.filter((q) => {
      const w = (q as any).showWhen;
      if (!w?.questionKey || !w.anyOf?.length) return true;
      const given = byKey[w.questionKey];
      const list = Array.isArray(given) ? given : given ? [given] : [];
      return w.anyOf.some((v: string) => list.includes(v));
    });
  }, [questions, answers]);

  const steps = useMemo<Step[]>(() => {
    const s: Step[] = [];
    if (collectContact) s.push({ type: "contact" });

    // Consecutive questions sharing a section become ONE step under that
    // heading. Questions with no section keep a card each, which is what every
    // template written before sections existed expects.
    let i = 0;
    while (i < visible.length) {
      const q = visible[i]!;
      const section = sectionOf(q);
      if (!section) {
        s.push({ type: "question", q, qIndex: i });
        i += 1;
        continue;
      }
      const group: Question[] = [];
      while (i < visible.length && sectionOf(visible[i]!) === section) {
        group.push(visible[i]!);
        i += 1;
      }
      // The heading and its intro sit on the first question of the section.
      // `section` itself ("1 · Your brand") is the short label and becomes the
      // eyebrow above the heading, the way the intake was written.
      const lead: any = group.find((g: any) => g.sectionTitle) || group[0];
      s.push({
        type: "section",
        eyebrow: section,
        title: String(lead?.sectionTitle || section),
        intro: String(lead?.sectionIntro || ""),
        questions: group,
      });
    }

    s.push({ type: "review" });
    return s;
  }, [collectContact, visible]);

  const [current, setCurrent] = useState(0);
  // Falls back to the review card rather than being optional: the index is
  // already clamped, and leaving it nullable breaks every narrowing below.
  const step: Step = steps[Math.min(current, steps.length - 1)] ?? { type: "review" };
  const totalQuestions = questions.length;
  const progress = steps.length > 1 ? Math.round((current / (steps.length - 1)) * 100) : 100;

  // Can the user leave the current step? (Required fields must be satisfied.)
  const canAdvance = (): boolean => {
    if (preview) return true;
    if (step.type === "contact" && clientInfo) {
      return (
        clientInfo.respondentName.trim() !== "" &&
        emailOk(clientInfo.respondentEmail.trim()) &&
        clientInfo.respondentPhone.trim() !== ""
      );
    }
    if (step.type === "question" && step.q.required) {
      return isAnswered(answers[step.q._id]);
    }
    // A section is complete when its required questions are. The optional ones
    // can be left, which is what optional means.
    if (step.type === "section") {
      return step.questions.every((q) => !q.required || isAnswered(answers[q._id]));
    }
    return true;
  };

  // Which step holds a given question. Edit on the review jumps here — a
  // position in the question list is no longer a position in the steps, now
  // that a section holds several questions and conditional ones drop out.
  const stepIndexOf = (q: Question) =>
    steps.findIndex(
      (s) =>
        (s.type === "question" && s.q._id === q._id) ||
        (s.type === "section" && s.questions.some((x) => x._id === q._id))
    );

  const goNext = () => setCurrent((c) => Math.min(c + 1, steps.length - 1));
  const goPrev = () => setCurrent((c) => Math.max(c - 1, 0));
  const jumpTo = (i: number) => setCurrent(Math.max(0, Math.min(i, steps.length - 1)));

  // Wrap the change handler so single-choice inputs auto-advance (a leading
  // step-by-step form convention). Only forward, only when the step is valid.
  const handleQuestionChange = (q: Question, val: string | string[]) => {
    onChange(q._id, val);
    const single = q.answerType === "radio" || q.answerType === "select" || q.answerType === "date";
    if (!preview && single && isAnswered(val) && current < steps.length - 1) {
      window.setTimeout(() => setCurrent((c) => (c < steps.length - 1 ? c + 1 : c)), 350);
    }
  };

  const advanceDisabled = !canAdvance();

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Header + progress */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{name || "Questionnaire"}</h1>
        {description && <p className="text-gray-600 mt-1 text-sm sm:text-base">{description}</p>}
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs font-semibold text-gray-500 mb-1.5">
            <span>
              {step.type === "section"
                ? step.eyebrow || step.title
                : step.type === "question"
                ? `Question ${step.qIndex + 1} of ${totalQuestions}`
                : step.type === "contact"
                ? "Your details"
                : "Review"}
            </span>
            <span>{progress}%</span>
          </div>
          <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#2462b2] to-[#1e55a0] rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* The card */}
      <div className="bg-white border border-gray-200 shadow-xl rounded-2xl p-6 sm:p-8 min-h-[280px] flex flex-col">
        {step.type === "contact" && clientInfo && setClientInfo && (
          <div className="space-y-4 flex-1">
            <h2 className="text-lg font-bold text-gray-800">Tell us who you are</h2>
            <InputField
              label="Full Name"
              placeholder="Enter your full name"
              value={clientInfo.respondentName}
              required
              onChange={(e) => setClientInfo((p) => ({ ...p, respondentName: e.target.value }))}
            />
            <InputField
              label="Email Address"
              type="email"
              placeholder="Enter your email"
              value={clientInfo.respondentEmail}
              required
              onChange={(e) => setClientInfo((p) => ({ ...p, respondentEmail: e.target.value }))}
            />
            <InputField
              label="Phone Number"
              type="tel"
              placeholder="Enter your phone number"
              value={clientInfo.respondentPhone}
              required
              onChange={(e) => setClientInfo((p) => ({ ...p, respondentPhone: e.target.value }))}
            />
          </div>
        )}

        {/* A named section: its heading, what it is for, then its questions
            together. Reading them as a group is the point — they were written
            as one part of a conversation. */}
        {step.type === "section" && (
          <div className="flex-1 flex flex-col">
            <div className="mb-5">
              {/* The short numbered label the intake was written with, above
                  the heading it belongs to. */}
              {step.eyebrow && step.eyebrow !== step.title && (
                <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#2462b2] mb-1.5">
                  {step.eyebrow}
                </p>
              )}
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{step.title}</h2>
              {step.intro && <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">{step.intro}</p>}
            </div>

            <div className="space-y-6">
              {step.questions.map((q) => (
                <div key={q._id}>
                  <label className="block text-[15px] font-semibold text-gray-900 mb-1">
                    {q.question || "Untitled question"}
                    {q.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {/* Guidance the template author wrote for this question —
                      usually the difference between an answer and a guess. */}
                  {(q as any).helpText && (
                    <p className="text-[12.5px] text-gray-500 mb-2 leading-snug">{(q as any).helpText}</p>
                  )}
                  <QuestionInput
                    question={q}
                    value={answers[q._id] ?? (q.answerType === "checkbox" || q.answerType === "cards-multi" || q.answerType === "multiselect" ? [] : "")}
                    onChange={(val) => onChange(q._id, val)}
                    disabled={preview}
                    required={q.required}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {step.type === "question" && (
          <div className="flex-1 flex flex-col">
            <div className="mb-4">
              <span className="inline-flex items-center justify-center w-9 h-9 bg-gradient-to-r from-[#2462b2] to-[#1e55a0] text-white rounded-full text-sm font-bold mb-3">
                {step.qIndex + 1}
              </span>
              <label className="block text-lg sm:text-xl font-bold text-gray-900">
                {step.q.question || "Untitled question"}
                {step.q.required && <span className="text-red-500 ml-1">*</span>}
              </label>
            </div>
            <QuestionInput
              question={step.q}
              value={answers[step.q._id] ?? (step.q.answerType === "checkbox" ? [] : "")}
              onChange={(val) => handleQuestionChange(step.q, val)}
              disabled={preview}
              required={step.q.required}
            />
            {step.q.required && (
              <p className="text-[11px] text-gray-400 mt-3">This question is required.</p>
            )}
          </div>
        )}

        {step.type === "review" && (
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="text-[#2462b2]" size={22} />
              <h2 className="text-lg font-bold text-gray-800">Review your answers</h2>
            </div>
            <div className="space-y-3">
              {/* Only what the client was actually asked. Listing the
                  conditional questions they never saw would fill the review
                  with "No answer provided" for questions that did not apply. */}
              {visible.map((q, i) => {
                const a = answers[q._id];
                const answered = isAnswered(a);
                const display = Array.isArray(a) ? a.join(", ") : (a as string);
                return (
                  <div
                    key={q._id}
                    className="flex items-start justify-between gap-3 border border-gray-200 rounded-lg p-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">
                        {i + 1}. {q.question}
                        {q.required && <span className="text-red-500 ml-1">*</span>}
                      </p>
                      <p className={`text-sm mt-0.5 ${answered ? "text-gray-700" : "text-gray-400 italic"}`}>
                        {answered ? display : "No answer provided"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => jumpTo(stepIndexOf(q))}
                      className="shrink-0 inline-flex items-center gap-1 text-xs font-bold text-[#2462b2] hover:underline"
                    >
                      <Pencil size={12} /> Edit
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between gap-3 mt-6">
        <button
          type="button"
          onClick={goPrev}
          disabled={current === 0}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        >
          <ArrowLeft size={18} /> Previous
        </button>

        {step.type !== "review" ? (
          <button
            type="button"
            onClick={goNext}
            disabled={advanceDisabled}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#2462b2] text-white font-bold hover:bg-[#1e55a0] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            Next <ArrowRight size={18} />
          </button>
        ) : preview ? (
          <button
            type="button"
            disabled
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gray-300 text-gray-500 font-bold cursor-not-allowed"
          >
            Submit (Preview Only)
          </button>
        ) : (
          <Button onClick={onSubmit} isLoading={isSubmitting} className="!py-2.5">
            <span className="inline-flex items-center gap-2">
              <Check size={18} /> {isSubmitting ? "Submitting..." : "Submit"}
            </span>
          </Button>
        )}
      </div>
    </div>
  );
};

export default CardLayout;
